import { Transition } from "@headlessui/react";
import { Fragment, useEffect } from "react";

interface Props {
  show?: boolean;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl";
  closeable?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

export default function Modal({
  children,
  show = false,
  maxWidth = "2xl",
  closeable = true,
  onClose = () => {},
}: Props) {
  const close = () => {
    if (closeable) {
      onClose();
    }
  };

  const maxWidthClass = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
  }[maxWidth];

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  return (
    <Transition show={show} as={Fragment} leave="duration-200">
      <div
        id="modal"
        className="fixed inset-0 flex items-center justify-center overflow-y-auto sm:px-0 z-50 transform transition-all"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={close}
          />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <button
            className="absolute top-4 right-4 text-white z-50"
            onClick={() => close()}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.0002 10.9724L5.72243 15.2502C5.58818 15.3845 5.42615 15.4516 5.23633 15.4516C5.04652 15.4516 4.88449 15.3845 4.75022 15.2502C4.61596 15.116 4.54883 14.9539 4.54883 14.7641C4.54883 14.5743 4.61596 14.4123 4.75022 14.278L9.02802 10.0002L4.75022 5.72243C4.61596 5.58818 4.54883 5.42615 4.54883 5.23633C4.54883 5.04652 4.61596 4.88449 4.75022 4.75022C4.88449 4.61596 5.04652 4.54883 5.23633 4.54883C5.42615 4.54883 5.58818 4.61596 5.72243 4.75022L10.0002 9.02802L14.278 4.75022C14.4123 4.61596 14.5743 4.54883 14.7641 4.54883C14.9539 4.54883 15.116 4.61596 15.2502 4.75022C15.3845 4.88449 15.4516 5.04652 15.4516 5.23633C15.4516 5.42615 15.3845 5.58818 15.2502 5.72243L10.9724 10.0002L15.2502 14.278C15.3845 14.4123 15.4516 14.5743 15.4516 14.7641C15.4516 14.9539 15.3845 15.116 15.2502 15.2502C15.116 15.3845 14.9539 15.4516 14.7641 15.4516C14.5743 15.4516 14.4123 15.3845 14.278 15.2502L10.0002 10.9724Z"
                fill="currentColor"
                stroke="black"
                strokeOpacity={0.4}
                offset={2}
              />
            </svg>
          </button>
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            className={`bg-white rounded-sm overflow-auto transform transition-all w-full max-h-screen ${maxWidthClass}`}
          >
            {children}
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
}
